import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  ConfidenceFactor,
  ConfidenceLevel,
  PartDemand,
} from '../../services/partDemandTypes';
import { percent } from '../quoteToCash/format';

import { formatAverage, formatQty } from './format';

const LEVEL_STYLE: Record<ConfidenceLevel, string> = {
  High: 'bg-green-100 text-green-800',
  Medium: 'bg-amber-100 text-amber-800',
  Low: 'bg-red-100 text-red-800',
};

const RATING_STYLE: Record<ConfidenceFactor['rating'], string> = {
  strong: 'text-green-700',
  good: 'text-green-700',
  limited: 'text-amber-700',
  weak: 'text-red-700',
};

/** The 3, 6 and 12-month statistical forecast, known demand and planning view, with the forecast's confidence */
export const ForecastTable: React.FC<{ data: PartDemand }> = ({ data }) => {
  const { t, i18n } = useTranslation();
  const { forecast, part } = data;
  const qty = (value: number) => formatQty(value, part.uom, i18n.language);
  const level = forecast.confidence.level;
  const levelLabel = t(`partDemand.confidence.level.${level}`);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-3 text-left font-semibold text-gray-700">
              {t('partDemand.forecast.horizon')}
            </th>
            <th
              className="px-4 py-3 text-right font-semibold text-gray-700"
              title={t('partDemand.forecast.statisticalHelp')}
            >
              {t('partDemand.forecast.statistical')}
            </th>
            <th
              className="px-4 py-3 text-right font-semibold text-gray-700"
              title={t('partDemand.forecast.knownHelp')}
            >
              {t('partDemand.forecast.known')}
            </th>
            <th
              className="px-4 py-3 text-right font-semibold text-gray-700"
              title={t('partDemand.forecast.planningViewHelp')}
            >
              {t('partDemand.forecast.planningView')}
            </th>
            <th className="px-4 py-3 text-left font-semibold text-gray-700">
              {t('partDemand.forecast.confidence')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {forecast.horizons.map((h) => (
            <tr key={h.months}>
              <td className="px-4 py-2 font-medium text-gray-900 whitespace-nowrap">
                {t('partDemand.forecast.next', { count: h.months })}
              </td>
              <td className="px-4 py-2 text-right tabular-nums">
                {h.statistical === null
                  ? t('partDemand.forecast.insufficient')
                  : qty(h.statistical)}
              </td>
              <td className="px-4 py-2 text-right tabular-nums">
                {qty(h.known)}
              </td>
              <td className="px-4 py-2 text-right tabular-nums font-semibold">
                {h.combined === null ? '—' : qty(h.combined)}
              </td>
              <td className="px-4 py-2">
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${LEVEL_STYLE[level]}`}
                >
                  {levelLabel}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** Statistical forecast, known demand and the planning view for 3, 6 and 12 months, and why the confidence is what it is */
export const ForecastSection: React.FC<{ data: PartDemand }> = ({ data }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { forecast } = data;
  const level = forecast.confidence.level;
  const levelLabel = t(`partDemand.confidence.level.${level}`);

  /** A factor's measure in words, e.g. "11 months of history" */
  const describe = (factor: ConfidenceFactor) => {
    const value = factor.value ?? 0;
    switch (factor.key) {
      case 'activeMonths':
        return t('partDemand.confidence.phrase.activeMonths', {
          active: data.kpis.activeMonths,
          months: data.kpis.analysisMonths,
        });
      case 'variability':
        return factor.value === null
          ? t('partDemand.confidence.phrase.noVariability')
          : t('partDemand.confidence.phrase.variability', {
              cv: formatAverage(value, lang),
            });
      case 'concentration':
        return factor.value === null
          ? t('partDemand.confidence.phrase.noCustomers')
          : t('partDemand.confidence.phrase.concentration', {
              pct: percent(value, lang, 0),
            });
      case 'recency':
        return factor.value === null
          ? t('partDemand.confidence.phrase.noDemand')
          : t('partDemand.confidence.phrase.recency', { count: value });
      default:
        return t(`partDemand.confidence.phrase.${factor.key}`, {
          count: value,
        });
    }
  };
  // Customer breadth repeats concentration when there are no customers
  const factors = forecast.confidence.factors.filter(
    (f) =>
      !(
        f.key === 'breadth' &&
        f.value === 0 &&
        data.kpis.topCustomerPct === null
      )
  );
  const strengths = factors.filter(
    (f) => f.rating === 'strong' || f.rating === 'good'
  );
  const weaknesses = factors.filter(
    (f) => f.rating === 'limited' || f.rating === 'weak'
  );
  const list = (items: ConfidenceFactor[]) => items.map(describe).join(', ');
  const summary = [
    strengths.length > 0 ? list(strengths) : '',
    weaknesses.length > 0
      ? `${strengths.length > 0 ? t('partDemand.confidence.but') : ''}${list(weaknesses)}`
      : '',
  ].join('');

  return (
    <>
      <ForecastTable data={data} />

      <div className="mt-4 p-4 rounded-lg border border-gray-200 bg-gray-50 text-sm">
        <p className="text-gray-900">
          <span className="font-semibold">
            {t('partDemand.confidence.title', {
              level: levelLabel,
              score: forecast.confidence.score,
            })}
          </span>{' '}
          {summary}.
        </p>
        <ul className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1">
          {factors.map((f) => (
            <li key={f.key} className="flex justify-between gap-2">
              <span className="text-gray-600">
                {t(`partDemand.confidence.factor.${f.key}`)}
              </span>
              <span className={`tabular-nums ${RATING_STYLE[f.rating]}`}>
                {formatAverage(f.points, lang)} / {f.weight}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <p className="mt-4 text-sm text-gray-600">
        <span className="font-medium text-gray-700">
          {t('partDemand.forecast.methodTitle')}
        </span>{' '}
        {forecast.monthlyForecast === null
          ? t('partDemand.forecast.methodNone')
          : t('partDemand.forecast.method', {
              baseline: formatAverage(forecast.baselineMonthly, lang),
              months: data.kpis.analysisMonths,
              trend:
                forecast.trendPct === null
                  ? t('partDemand.forecast.noTrend')
                  : t('partDemand.forecast.trend', {
                      trend: percent(forecast.trendPct, lang, 0),
                      applied: percent(forecast.appliedTrendPct, lang, 0),
                    }),
              monthly: formatAverage(forecast.monthlyForecast, lang),
            })}{' '}
        {t('partDemand.forecast.doubleCount')}
      </p>
    </>
  );
};
